import React from 'react';

import { useIntl } from 'react-intl';

import { useDeletePricingPlanTickRate } from 'src/features/pricingPlan/api/tickRate/useDeletePricingPlanTickRate';
import { LIST_PRICING_PLAN_TICK_RATES_RESOURCE } from 'src/features/pricingPlan/api/tickRate/useListPricingPlanTickRates';
import { pricingPlanMessages } from 'src/features/pricingPlan/utils/messages';
import { PricingPlanTickRate } from 'src/features/pricingPlan/utils/types';
import { toast } from 'src/hooks/UseToast';
import { queryClient } from 'src/lib/react-query';
import { useParking } from 'src/providers/ParkingProvider';
import ConfirmationModal from 'src/registry/components/blocks/ConfirmationModal';
import { DataTableNewEntryProps } from 'src/registry/components/table/types';
import { generalErrorMessages, generalMessages } from 'src/registry/utils/messages';
import { ICON_MAP } from 'src/utils/constants';

interface PricingPlanTickRateFormModalProps<TData> extends DataTableNewEntryProps<TData> {
    selectedRate: PricingPlanTickRate;
    children?: React.ReactNode;
}

function DeletePricingPlanTickRateFormModal<TData>({
    selectedRate,
    children,
}: PricingPlanTickRateFormModalProps<TData>) {
    const [isModalOpen, setIsModalOpen] = React.useState(false);
    const { formatMessage } = useIntl();
    const { parkingId } = useParking();

    const { mutate: deleteTickRate } = useDeletePricingPlanTickRate({
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: [LIST_PRICING_PLAN_TICK_RATES_RESOURCE] });
            setIsModalOpen(false);
            toast({
                title: formatMessage(pricingPlanMessages.deleteTickRateSuccess),
                description: formatMessage(pricingPlanMessages.deleteTickRateSuccessDescription),
            });
        },
        onError: (error) => {
            if (error?.response?.status !== 400) return;
            const errorDetails = error.response?.data?.errors[0]?.detail;
            const errorCode = formatMessage(generalErrorMessages.somethingWentWrong);
            const errorMessage = errorDetails || formatMessage(generalErrorMessages.unknownError);

            toast({
                title: errorCode,
                description: errorMessage,
                variant: 'destructive',
            });
        },
    });

    return (
        <ConfirmationModal
            isModalOpen={isModalOpen}
            setIsModalOpen={setIsModalOpen}
            dialogDetails={{
                title: { ...generalMessages.delete },
                subTitle: { ...pricingPlanMessages.deleteTickRateSubtitle },
                icon: ICON_MAP.DELETE,
                confirmationMessage: { ...pricingPlanMessages.confirmDeleteTickRate },
            }}
            callbackButton={{
                variant: 'destructiveLight',
                buttonTitle: generalMessages.delete,
                callbackFn: () => {
                    return (
                        deleteTickRate({ planId: selectedRate.pricing_plan, tickRateId: selectedRate.id, parkingId }),
                        setIsModalOpen(false)
                    );
                },
            }}
            children={children}
        />
    );
}

export default DeletePricingPlanTickRateFormModal;
